/**
 * Fresh-load-on-release.
 *
 * Each build stamps `__APP_VERSION__` (vite.config.ts) and emits a non-cached
 * `version.json`. We poll that manifest; when the deployed version differs from the one
 * this tab is running, we hard-reload at the next safe moment — returning to the tab or
 * the next route change — so a long-lived SPA session picks up new UI, assets and product
 * images (image URLs carry `?r=<version>`, so a fresh load fetches the new ones) without a
 * manual refresh. HTTP caching already handles fresh navigations; this covers open tabs.
 */
const RUNNING_VERSION = __APP_VERSION__;
const VERSION_URL = `${import.meta.env.BASE_URL}version.json`;
const POLL_MS = 5 * 60_000;
const RELOAD_THROTTLE_MS = 30_000;

let updatePending = false;
let started = false;

export function isReleasePending(): boolean {
  return updatePending;
}

/** Reload at most once per throttle window, so a bad manifest can never loop-reload. */
function safeReload(): void {
  const now = Date.now();
  const last = Number(sessionStorage.getItem('skytech:lastReload') ?? 0);
  if (now - last < RELOAD_THROTTLE_MS) return;
  sessionStorage.setItem('skytech:lastReload', String(now));
  window.location.reload();
}

/** Called on route changes: reload only if a new release was already detected. */
export function reloadIfReleasePending(): void {
  if (updatePending) safeReload();
}

async function checkForRelease(): Promise<void> {
  try {
    const res = await fetch(`${VERSION_URL}?_=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const data = (await res.json()) as { version?: string };
    if (data.version && data.version !== RUNNING_VERSION) updatePending = true;
  } catch {
    // Offline, or no manifest (dev). Never disrupt the app on a failed probe.
  }
}

export function startReleaseWatcher(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  void checkForRelease();
  window.setInterval(() => void checkForRelease(), POLL_MS);

  // Returning to the tab is a natural refresh point: re-check, then reload if stale.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkForRelease().then(reloadIfReleasePending);
    }
  });

  // A dynamic import that 404s almost always means the old chunk was replaced by a deploy.
  window.addEventListener('vite:preloadError', () => safeReload());
}
